import {
  PreProposalData,
  RequiredDocuments
} from "../../../store/preProposalStore";
import {
  LimitReassessmentEnum,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import { ROUTES } from "@repo/core/routes";
import type { ProposalPhoneTokenStatusResponseDTO } from "@repo/core/services/entities/WayupRequisitions";
import {
  hasReusableProposalPhoneTokenValidation,
  isDraftProposalForPhoneTokenStatus
} from "../../../services/proposalPhoneTokenStatus.service";

/** Detects credit reanalysis from the persisted proposal field. */
export const isReanalysisProposal = (
  proposalData?: PreProposalData | null
): boolean =>
  proposalData?.strReavaliacaoLimite === LimitReassessmentEnum.REANALYSIS;

export const shouldConfirmTokenWhenKeepingCurrentPhone = (
  proposalData?: PreProposalData | null,
  tokenStatus?: ProposalPhoneTokenStatusResponseDTO | null
): boolean => {
  if (tokenStatus) {
    return !hasReusableProposalPhoneTokenValidation(proposalData, tokenStatus);
  }

  if (proposalData?.lngProposta) {
    return false;
  }

  if (isDraftProposalForPhoneTokenStatus(proposalData)) {
    return false;
  }

  return isReanalysisProposal(proposalData);
};

export const shouldNavigateToCreditProposalFlow = (
  proposalData?: PreProposalData | null
): boolean =>
  isReanalysisProposal(proposalData) ||
  proposalData?.queueEditMode === "proposal";

type NavigateFn = (route: string, params?: Record<string, unknown>) => void;

/** Redirects to credit proposal flow when applicable; returns true if navigated. */
export const navigateToReanalysisCreditFlowIfApplicable = (
  proposalData: PreProposalData | null | undefined,
  navigate: NavigateFn,
  clearCreditProposal: () => void
): boolean => {
  if (
    !shouldNavigateToCreditProposalFlow(proposalData) ||
    !proposalData?.lngProposta
  ) {
    return false;
  }

  clearCreditProposal();
  navigate(ROUTES.creditProposalPersonalData, {
    proposalId: String(proposalData.lngProposta)
  });
  return true;
};

export const hasUnicoDocuments = (
  requiredDocuments?: RequiredDocuments[]
): boolean => {
  if (!requiredDocuments) return false;

  const mandatoryIncludesBiometry = requiredDocuments.some(
    (doc) =>
      String(doc.strTipoDocumento).trim() === RequiredDocumentTypeEnum.BIOMETRY
  );

  if (!mandatoryIncludesBiometry) {
    return false;
  }

  return requiredDocuments.some((doc) => {
    const docType = String(doc.strTipoDocumento).trim();
    return (
      docType === RequiredDocumentTypeEnum.DOCUMENT_FRONT ||
      docType === RequiredDocumentTypeEnum.DOCUMENT_BACK ||
      docType === RequiredDocumentTypeEnum.BIOMETRY
    );
  });
};

export const shouldNavigateToManualCapture = (
  requiredDocuments?: RequiredDocuments[]
): boolean => {
  return hasUnicoDocuments(requiredDocuments);
};

export const shouldNavigateToFinancialService = (
  requiredDocuments?: RequiredDocuments[]
): boolean => {
  return !requiredDocuments || requiredDocuments.length === 0;
};

export const navigateBasedOnDocuments = (
  requiredDocuments: RequiredDocuments[] | undefined,
  push: (route: string, params?: Record<string, unknown>) => void,
  hasUnicoDocumentsFlag?: boolean,
  proposalData?: PreProposalData | null,
  replace?: (route: string, params?: Record<string, unknown>) => void,
  clearCreditProposal?: () => void,
  resetTo?: (route: string, params?: Record<string, unknown>) => void
): void => {
  if (clearCreditProposal) {
    const navigate = resetTo ?? replace ?? push;
    if (
      navigateToReanalysisCreditFlowIfApplicable(
        proposalData,
        navigate,
        clearCreditProposal
      )
    ) {
      return;
    }
  }

  const navigate = replace ?? push;
  const hasUnico =
    hasUnicoDocumentsFlag ?? hasUnicoDocuments(requiredDocuments);

  if (hasUnico) {
    navigate(ROUTES.preProposalsSelectDocuments, {
      biometryFailed: true
    });
    return;
  }

  const needsManualIdentityDocuments =
    requiredDocuments?.some((doc) => {
      const docType = String(doc.strTipoDocumento).trim();
      return (
        docType === RequiredDocumentTypeEnum.DOCUMENT_FRONT ||
        docType === RequiredDocumentTypeEnum.DOCUMENT_BACK
      );
    }) ?? false;

  if (needsManualIdentityDocuments) {
    push(ROUTES.preProposalsSelectDocuments, {
      forceManualCapture: true
    });
    return;
  }

  //TODO REMOVE WHEN preProposalsSelectFinancialService THE FLOW RETURNS
  // push(ROUTES.preProposalsSelectFinancialService);
  push(ROUTES.preProposalsVerifyInformations);
};
